export interface LimitWritingOptions {
	"handle-tab"?: boolean;
	"max-length"?: number;
	"big-change-threshold"?: number;
	"big-change-message"?: string;
	"text-limit-message"?: string;
	unmount?: boolean;
}

export default function limitWriting(
	elems: NodeListOf<HTMLTextAreaElement> | undefined,
	options: LimitWritingOptions
) {
	// Create settings variable containing the default values overridden by values in the options argument.
	const settings = {
		"handle-tab": true,
		"max-length": 25000,
		"big-change-threshold": -1,
		"big-change-message": "You just removed a large portion of text. Did you mean to do this?",
		"text-limit-message": "Your text has been shortened due to exceeding the maximum length.",
		unmount: false,
	};

	Object.assign(settings, options);

	if (settings["unmount"] === true) {
		elems?.forEach((elem: HTMLTextAreaElement) => {
			elem.removeEventListener("input", handleChangeEvent);
			elem.removeEventListener("keydown", handleTab);
		});
		clearTimeout(validateTimeoutId);
		return;
	}

	elems?.forEach((elem: HTMLTextAreaElement) => {
		elem.dataset.maxlength = settings["max-length"].toString();
		elem.dataset.oldvalue = elem.value;
		elem.addEventListener("input", handleChangeEvent);

		if (settings["handle-tab"] == true) {
			elem.addEventListener("keydown", handleTab);
		}
	});

	var validateTimeoutId: NodeJS.Timeout | undefined = undefined;
	var eventTarget: HTMLTextAreaElement | undefined = undefined;

	function handleChangeEvent(this: HTMLTextAreaElement, event: Event) {
		clearTimeout(validateTimeoutId);
		eventTarget = this;

		validateTimeoutId = setTimeout(function () {
			if (!eventTarget) return;
			validateEssayChange(
				eventTarget,
				settings["max-length"],
				settings["big-change-threshold"],
				settings["big-change-message"],
				settings["text-limit-message"]
			);
		}, 500);
	}

	function handleTab(this: HTMLTextAreaElement, event: KeyboardEvent) {
		if (event.key == "Tab") {
			event.preventDefault();

			const scrollTop = this.scrollTop;
			const start = this.selectionStart;
			const head = this.value.substring(0, start);
			const tail = this.value.substring(this.selectionEnd);
			this.value = head + "\t" + tail;
			this.selectionStart = start + 1;
			this.selectionEnd = start + 1;

			// Firefox scrolls back to the top when we modify the text, so let's bump
			// it back to where it was.
			this.scrollTop = scrollTop;
			return false;
		}
	}

	// Check that the essay is not too long and that the user didn't accidentally delete too much.
	function validateEssayChange(
		textArea: HTMLTextAreaElement,
		maxLength: number,
		bigChangeThreshold: number,
		bigChangeMessage: string,
		textLimitMessage: string
	) {
		const essay = textArea.value;
		if (essay.length > maxLength) {
			const shortenedEssay = essay.substring(0, maxLength);
			textArea.value = shortenedEssay;
			alert(textLimitMessage);
		} else if (bigChangeThreshold > -1) {
			const oldEssay = textArea.dataset.oldvalue ?? "";
			const diff = essay.length - oldEssay.length;
			if (diff < -bigChangeThreshold && !confirm(bigChangeMessage)) {
				textArea.value = oldEssay;
			}
		}

		textArea.dataset.oldvalue = textArea.value;
	}
}
